import type { BookGenre } from '@booknest/shared';

import { GENRE_META } from '../genre-meta.js';

interface GenreChipsProps {
  /** Genres effectivement rendus dans la page, dans leur ordre. La puce
   *  d'un genre absent est désactivée. */
  availableGenres: ReadonlyArray<BookGenre>;
}

/** Renvoie l'id DOM stable d'une étagère de genre. */
export function genreShelfId(genre: BookGenre): string {
  return `shelf-${genre}`;
}

/**
 * Chips de navigation : un click scrolle vers l'étagère du genre. Les chips
 * sont rendues dans le même ordre que les étagères côté backend (affinité
 * décroissante puis alpha).
 */
export function GenreChips({ availableGenres }: GenreChipsProps): JSX.Element {
  const available = new Set(availableGenres);
  const orderedMeta = availableGenres
    .map((g) => GENRE_META.find((m) => m.value === g))
    .filter((m): m is (typeof GENRE_META)[number] => m !== undefined);

  const scrollToGenre = (genre: BookGenre): void => {
    if (!available.has(genre)) return;
    const el = document.getElementById(genreShelfId(genre));
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="flex flex-wrap gap-2 pb-1">
      {orderedMeta.map((c) => (
        <button
          key={c.value}
          type="button"
          onClick={() => scrollToGenre(c.value)}
          className="flex-shrink-0 rounded-full bg-paper px-3.5 py-2 text-xs font-semibold text-brown ring-1 ring-cream-dark transition-colors hover:bg-cream-dark hover:ring-brown-soft"
        >
          <span className="mr-1" aria-hidden>
            {c.emoji}
          </span>
          {c.label}
        </button>
      ))}
    </div>
  );
}
